/**
 * The phone rule (shared-react-ui#81, shared-audit#17 › Phone table): a
 * number is held to its COUNTRY's rules, not to E.164's shape — the owner
 * typed sixteen digits for the United Kingdom and the page took them.
 *
 * Ported from shared-react-auth `src/phone.ts` (`composeE164`,
 * `composeInternationalPhone`, `normalisePhoneInput`, `maxPhoneInputLength`,
 * 0.52.1 with #150's Italian plan) into ONE function, `checkPhone`, and
 * mirrored line for line by shared-pweb-common `pweb_common.fields.check_phone`.
 * The shared `cases.json` runs against both.
 *
 * The table (`phones.json`) is a byte-identical copy of shared-pweb-common
 * `src/pweb_common/fields_phones.json`, generated once at dev time from
 * libphonenumber metadata (237 offered countries, ~14 KB) plus the tracking
 * issue's hand-written mobile ranges. It is data, never a dependency:
 * libphonenumber-js would be ~80 KB in the widget.
 */
import table from './phones.json';
import type { FieldParams, FieldResult, PhoneKind, RequiredOption } from './types';
import { ok } from './rules';

type TableRow = [dial: string, anyMin: number, anyMax: number, mobileMin: number, mobileMax: number, trunk: string, keepsZero: number, example: string];

interface PhoneTable {
  source: string;
  countries: Record<string, TableRow>;
  mobileRules: Record<string, { min: number; max: number; prefixes: string[] }>;
  nanp: { dial: string; min: number; max: number };
  primaryForDial: Record<string, string>;
  leadingZeroIsTheNumber: string[];
  /** kind → the countries where a national number may BEGIN 00 (libphonenumber's per-type patterns). */
  doubleZero: Record<PhoneKind, string[]>;
}

const TABLE = table as unknown as PhoneTable;
const LEADING_ZERO_IS_THE_NUMBER = new Set(TABLE.leadingZeroIsTheNumber);
const DOUBLE_ZERO: Record<PhoneKind, ReadonlySet<string>> = {
  any: new Set(TABLE.doubleZero.any),
  mobile: new Set(TABLE.doubleZero.mobile),
};

/** Where the lengths come from ('libphonenumber-js 1.13.14'). */
export const PHONE_TABLE_SOURCE: string = TABLE.source;

/** A country the phone boxes offer. */
export interface PhoneCountry {
  /** ISO 3166-1 alpha-2, e.g. 'GB'. */
  iso: string;
  /** E.164 country calling code, e.g. '+44'. */
  dial: string;
}

/** Every offered country, in the table's order. */
export const PHONE_COUNTRIES: readonly PhoneCountry[] = Object.entries(TABLE.countries).map(([iso, row]) => ({
  iso,
  dial: row[0],
}));

/** Dial codes without the '+', for reading one off the front of a number. */
const DIAL_DIGITS = new Set(PHONE_COUNTRIES.map((c) => c.dial.slice(1)));

/** What the rules know about one country's numbers, for one kind. */
export interface PhoneRule {
  country: string;
  dial: string;
  /** National significant number lengths (digits after the dial code, no trunk prefix). */
  min: number;
  max: number;
  /** The trunk prefix dialled at home: '0', '1', '8', '06' or ''. */
  trunk: string;
  /** A leading 0 is part of the number (mobiles that begin with one; Italy's plan): never stripped. */
  keepsZero: boolean;
  /** `mobile` only, where the plan says so: the digits a mobile starts with. */
  prefixes?: readonly string[];
  /** North American plan: area code and exchange must start 2-9. */
  nanp?: boolean;
  /** An example mobile, grouped the national way — a box's placeholder. '' if none. */
  example: string;
  /**
   * A national number of this kind may begin 00 (Tajik mobiles, Japan's 0037
   * free-dial numbers). After the dial code a 00 is never an international
   * prefix: here it is the number's own, elsewhere the number is not valid.
   */
  doubleZero?: boolean;
}

/** The country a dial code most likely means ('+44' → 'GB'), '' for none. */
export function primaryCountryForDial(dial: string): string {
  return TABLE.primaryForDial[dial] ?? PHONE_COUNTRIES.find((c) => c.dial === dial)?.iso ?? '';
}

/**
 * The rule for an offered country, undefined for any other. `any`: the
 * country's lengths for every number. `mobile`: the hand-written mobile range
 * of the markets we sell in, the North American plan for every +1 country,
 * and the generated mobile lengths everywhere else — with no leading-digit
 * check there: a wrongly refused real number costs more than a landline that
 * receives no text.
 */
export function phoneRule(country: string, kind: PhoneKind = 'mobile'): PhoneRule | undefined {
  const iso = String(country ?? '').toUpperCase();
  const row = TABLE.countries[iso];
  if (!row) return undefined;
  const [dial, anyMin, anyMax, mobileMin, mobileMax, trunk, keepsZero, example] = row;
  const base: PhoneRule = {
    country: iso,
    dial,
    min: anyMin,
    max: anyMax,
    trunk,
    keepsZero: keepsZero === 1 || LEADING_ZERO_IS_THE_NUMBER.has(iso),
    example,
    doubleZero: DOUBLE_ZERO[kind === 'any' ? 'any' : 'mobile'].has(iso),
  };
  if (kind === 'any') return base;
  if (dial === TABLE.nanp.dial) return { ...base, min: TABLE.nanp.min, max: TABLE.nanp.max, nanp: true };
  const mobile = TABLE.mobileRules[iso];
  if (mobile) return { ...base, min: mobile.min, max: mobile.max, prefixes: mobile.prefixes };
  return { ...base, min: mobileMin, max: mobileMax };
}

/**
 * The rule an international number with dial code `dial` ('+44') is held to
 * when no country was chosen: the widest lengths of the countries sharing the
 * code, the main country's trunk prefix, no mobile range; a leading 00 where
 * any of them has one. Undefined for a code no offered country has.
 */
export function internationalPhoneRule(dial: string, kind: PhoneKind = 'any'): PhoneRule | undefined {
  const sharing = PHONE_COUNTRIES.filter((c) => c.dial === dial).map((c) => phoneRule(c.iso, kind)!);
  const primary = phoneRule(primaryCountryForDial(dial), kind);
  if (sharing.length === 0 || !primary) return undefined;
  return {
    ...primary,
    min: Math.min(...sharing.map((r) => r.min)),
    max: Math.max(...sharing.map((r) => r.max)),
    prefixes: undefined,
    nanp: undefined,
    doubleZero: sharing.some((r) => r.doubleZero),
  };
}

// E.164: at most 15 digits with the country code; the estate's floor is 7.
// The table's lengths are already held to these (the generator clamps them),
// so this is the backstop, and it names the side a number fails on.
const E164_MIN_DIGITS = 7;
const E164_MAX_DIGITS = 15;
// The white-space set of rules.ts (pweb_common.fields › _WS), plus ( ) . -
const WS = ' \\t\\n\\v\\f\\r\\u00a0\\u1680\\u2000-\\u200a\\u2028\\u2029\\u202f\\u205f\\u3000';
const PHONE_CHARACTERS = new RegExp(`^\\+?[0-9${WS}().-]*$`);
const PHONE_SEPARATORS = new RegExp(`[${WS}().-]`, 'g');
const WS_EDGES = new RegExp(`^[${WS}]+|[${WS}]+$`, 'g');

/** The dial code (no '+') at the front of `digits`, longest first. */
function leadingDial(digits: string): string {
  for (let n = 3; n >= 1; n -= 1) {
    if (digits.length >= n && DIAL_DIGITS.has(digits.slice(0, n))) return digits.slice(0, n);
  }
  return '';
}

function refuse(
  code: 'phone.required' | 'phone.invalid_characters' | 'phone.needs_country_code' | 'phone.unknown_country_code'
    | 'phone.country_mismatch' | 'phone.too_long' | 'phone.too_short' | 'phone.not_mobile' | 'phone.not_valid',
  rule: PhoneRule | undefined,
  kind: PhoneKind,
  value: string,
  extra: FieldParams = {},
): FieldResult {
  const params: FieldParams = { kind };
  if (rule) {
    const home = rule.keepsZero ? '' : rule.trunk === '0' ? '0' : '';
    Object.assign(params, {
      country: rule.country,
      dial: rule.dial,
      min: rule.min,
      max: rule.max,
      trunk: rule.keepsZero ? '' : rule.trunk,
    });
    if (rule.prefixes?.length) params.prefixes = rule.prefixes.map((p) => home + p);
  }
  return { ok: false, value, problem: { code, params: { ...params, ...extra } } };
}

/**
 * Drop the trunk prefix a person types at home: ONE leading 0 where the trunk
 * is '0' (or there is none — a stray 0 goes, as it always did); never where
 * the 0 is the number's (`keepsZero`); a longer or non-zero trunk ('1', '8',
 * '06') only when what remains is a whole national number.
 */
function stripTrunk(digits: string, rule: PhoneRule): string {
  if (rule.keepsZero) return digits;
  if (rule.trunk === '0' || rule.trunk === '') return digits.replace(/^0/, '');
  if (digits.startsWith(rule.trunk)) {
    const rest = digits.slice(rule.trunk.length);
    if (rest.length >= rule.min && rest.length <= rule.max) return rest;
  }
  return digits;
}

/** `phone.too_long` over E.164's 15 digits, `phone.too_short` under the floor of 7 (the code counts). */
function e164Problem(dial: string, national: string): 'phone.too_long' | 'phone.too_short' | undefined {
  const digits = dial.replace(/^\+/, '').length + national.length;
  if (digits > E164_MAX_DIGITS) return 'phone.too_long';
  if (digits < E164_MIN_DIGITS) return 'phone.too_short';
  return undefined;
}

/**
 * The country's verdict on the digits after the dial code (`afterCode`) or
 * typed at home. Only after the code can a leading 00 not be an international
 * prefix: it is the number's own where the plan has such numbers
 * (`doubleZero`) and `phone.not_valid` everywhere else.
 */
function checkNational(digits: string, rule: PhoneRule, kind: PhoneKind, typed: string, afterCode: boolean): FieldResult {
  let national: string;
  if (afterCode && digits.startsWith('00')) {
    if (!rule.doubleZero) return refuse('phone.not_valid', rule, kind, typed, { startsWith: '00' });
    national = digits;
  } else {
    national = stripTrunk(digits, rule);
  }
  if (national.length > rule.max) return refuse('phone.too_long', rule, kind, typed);
  if (rule.nanp && /^[01]/.test(national)) return refuse('phone.not_valid', rule, kind, typed);
  if (rule.prefixes && national.length > 0) {
    // Wrong as soon as no mobile range can still match what is there.
    const possible = rule.prefixes.some((p) => national.startsWith(p) || p.startsWith(national));
    if (!possible) return refuse('phone.not_mobile', rule, kind, typed);
  }
  if (national.length < rule.min) return refuse('phone.too_short', rule, kind, typed);
  if (rule.nanp && /^[01]/.test(national.slice(3))) return refuse('phone.not_valid', rule, kind, typed);
  // Whatever a table says, E.164 is 7 to 15 digits.
  const problem = e164Problem(rule.dial, national);
  if (problem) return refuse(problem, rule, kind, typed);
  return ok(`${rule.dial}${national}`);
}

/**
 * A telephone number in E.164 (`+<dial><national>`).
 *
 * With `country` (ISO alpha-2, an offered one) the number may be typed
 * nationally ('07717 502444') or with that country's own code ('+44 …',
 * '0044 …'); another country's code is refused by name
 * (`phone.country_mismatch`). Without it the number must start with `+`/`00`
 * and a code (`phone.needs_country_code`). A country the table does not offer
 * is treated as none.
 *
 * `kind` `mobile` (a number we text): the country's mobile lengths and, where
 * the tracking issue's table says so, the digits mobiles start with. `any` (a
 * number we record): the country's full length range.
 *
 * `checkPhone('+44 7717502444444444', {country: 'GB', kind: 'mobile'})` →
 * `phone.too_long` with `{country: 'GB', dial: '+44', max: 10}`.
 */
export function checkPhone(
  value: unknown,
  options: { country?: string; kind?: PhoneKind } & RequiredOption = {},
): FieldResult {
  const kind = options.kind ?? 'mobile';
  if (kind !== 'mobile' && kind !== 'any') throw new Error(`unknown phone kind ${String(kind)}`);
  const chosen = options.country ? phoneRule(options.country, kind) : undefined;
  let typed: string;
  if (value === null || value === undefined) typed = '';
  else if (typeof value === 'string') typed = value.replace(WS_EDGES, '');
  else if (typeof value === 'number' && Number.isInteger(value)) typed = String(value);
  else return refuse('phone.invalid_characters', chosen, kind, '');
  if (!typed) return options.required === false ? ok('') : refuse('phone.required', chosen, kind, '');
  if (!PHONE_CHARACTERS.test(typed)) return refuse('phone.invalid_characters', chosen, kind, typed);

  let digits = typed.replace(PHONE_SEPARATORS, '');
  const international = digits.startsWith('+') ? digits.slice(1) : digits.startsWith('00') ? digits.slice(2) : null;

  if (chosen) {
    const rule = chosen;
    const own = rule.dial.slice(1);
    let afterCode = false;
    if (international !== null) {
      if (international.startsWith(own)) {
        digits = international.slice(own.length);
        afterCode = true;
      } else if (own.startsWith(international)) {
        // '+4' on its way to '+44': nothing to read yet.
        return refuse('phone.too_short', rule, kind, typed);
      } else {
        const other = leadingDial(international);
        if (!other) return refuse('phone.unknown_country_code', rule, kind, typed, { otherDial: `+${international.slice(0, 3)}` });
        return refuse('phone.country_mismatch', rule, kind, typed, {
          otherDial: `+${other}`,
          otherCountry: primaryCountryForDial(`+${other}`),
        });
      }
    }
    return checkNational(digits, rule, kind, typed, afterCode);
  }

  if (international === null) return refuse('phone.needs_country_code', undefined, kind, typed);
  const code = leadingDial(international);
  if (!code) return refuse('phone.unknown_country_code', undefined, kind, typed, { otherDial: `+${international.slice(0, 3)}` });
  const rule = internationalPhoneRule(`+${code}`, kind)!;
  const rest = international.slice(code.length);
  let national: string;
  if (rest.startsWith('00')) {
    // After the code a 00 is never an international prefix.
    if (!rule.doubleZero) return refuse('phone.not_valid', rule, kind, typed, { startsWith: '00' });
    national = rest;
  } else {
    national = rule.trunk === '0' && !rule.keepsZero ? rest.replace(/^0/, '') : rest;
  }
  const e164 = e164Problem(rule.dial, national);
  if (national.length > rule.max || e164 === 'phone.too_long') return refuse('phone.too_long', rule, kind, typed);
  if (national.length < rule.min || e164 === 'phone.too_short') return refuse('phone.too_short', rule, kind, typed);
  if (kind === 'mobile') {
    // A number we text, with no country chosen: the code's main country's
    // MOBILE rule too (its range, the North American digits), as the
    // console's mobile boxes (mobileNumber.ts) and shared-auth's check_phone
    // apply it — '+44 20 …' is a landline. The digits after the code go in
    // as they are, so a 00 there is never re-read as an international prefix.
    return checkNational(rest, phoneRule(primaryCountryForDial(`+${code}`), 'mobile')!, 'mobile', typed, true);
  }
  return ok(`+${code}${national}`);
}

/**
 * The `maxLength` of a national-number box: the longest national number,
 * plus the trunk prefix people type at home, plus three spaces (UK: 10 + '0'
 * + 3 = 14). A pasted '+44 …' never meets it — the box strips the code on
 * paste (`normalisePhoneInput`). 20 for a box that takes an international
 * number ('+44 (0)20 7946 0000' and room to spare).
 */
export function phoneInputMaxLength(country?: string, kind: PhoneKind = 'mobile'): number {
  const rule = country ? phoneRule(country, kind) : undefined;
  if (!rule) return 20;
  return rule.max + (rule.keepsZero ? 0 : rule.trunk.length) + 3;
}

/** The country's example mobile for a box's placeholder, '' when the table has none. */
export function phonePlaceholder(country: string): string {
  return phoneRule(country)?.example ?? '';
}

/**
 * What goes in a national-number box when something is typed or pasted: the
 * characters a number is written with (a letter never is), and a '+CC' /
 * '00CC' in front dropped when it is the chosen country's own — "+44 7717…"
 * with the United Kingdom chosen is "7717…". A different code is LEFT, so
 * `checkPhone` can name that country. `stripped` says a code was dropped, so
 * the page can say it adds the code itself (`phoneCodeAddedMessage`).
 */
export function normalisePhoneInput(raw: string, dial: string): { value: string; stripped: boolean } {
  const kept = raw.replace(/[^\d\s().+-]/g, '');
  const lead = kept.match(/^\s*\+/) ? '+' : '';
  const value = lead + kept.replace(/\+/g, '').replace(/^\s+/, '');
  const own = dial.replace(/^\+/, '');
  // Walk the front of the value: '+' or '00', then the dial digits, allowing
  // the separators people type between them ('+44 (0) 7…', '0044-7…').
  const match = value.match(/^(\+|00)([\d\s().-]*)$/);
  if (!match || !own) return { value, stripped: false };
  let digitsSeen = '';
  let cut = -1;
  const body = match[2];
  for (let i = 0; i < body.length; i += 1) {
    if (/\d/.test(body[i])) {
      digitsSeen += body[i];
      if (digitsSeen === own) {
        cut = i + 1;
        break;
      }
      if (!own.startsWith(digitsSeen)) break;
    }
  }
  if (cut < 0) return { value, stripped: false };
  const rest = body.slice(cut).replace(/^[\s.-]+/, '');
  // Only once something follows the code: '+44' alone is still being typed.
  if (!/\d/.test(rest)) return { value, stripped: false };
  return { value: rest, stripped: true };
}

/**
 * The country's name in `locale`, from the runtime (Intl.DisplayNames): in
 * English when the locale is one no runtime accepts, and the ISO code where
 * the runtime has no region names at all — a code is an honest label, a
 * guessed name is not.
 */
export function countryName(iso: string, locale: string): string {
  for (const locales of [[locale || 'en', 'en'], ['en']]) {
    try {
      return new Intl.DisplayNames(locales, { type: 'region' }).of(iso) ?? iso;
    } catch {
      // next
    }
  }
  return iso;
}
